import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { TableContainer, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import {
  SingleClaimTable,
  CLAIM_MODE_VIEW,
  CLAIM_MODE_EDIT,
  CLAIM_MODE_NEW_CLAIM,
  CLAIM_MODE_NEW_ENTITY,
} from '../components/claim/singleClaimTable';
import {
  TRANSFER_PLAYER_SCHEMA,
  TRANSFER_PLAYER_CLAIM_SCHEMA,
  MAPPED_TRANSFER_PLAYER_SCHEMA,
} from '../schema/transferPlayerSchema';
import { ClaimFilters } from '../components/claim/claimFilters';
import ClaimHistoryTable from '../components/table/claimHistoryTable';
import { Progress } from '../components/util/progress';
import {
  TRANSFER_PLAYERS_AND_CLAIMS,
  SAVE_TRANSFER_PLAYER_CLAIM,
  OVERRIDE_TRANSFER_PLAYER_CLAIM,
  INSERT_GOLDEN_TRANSFER_PLAYER,
} from '../hasura/queries/transferPlayer/transferPlayerClaims';
import { Error } from '../components/util/notifications';
import { blankSchema } from '../util/schema';
import MappingView from '../components/mapping/mappingView';
import { DELETE_TRANSFER_PLAYER } from '../hasura/queries/transferPlayer/transferPlayers';
import {
  VIEW_TRANSFER_PLAYER_MAPPINGS,
  DELETE_TRANSFER_PLAYER_MAPPING,
} from '../hasura/queries/transferPlayer/transferPlayerMappings';

const useStyle = makeStyles({
  root: {
    marginTop: 20,

    '& h3': {
      paddingLeft: 5,
    },
  },
});
const useStyleButton = makeStyles({
  button: { margin: 5, float: 'right' },
  clearButton: {
    '&:hover': {
      backgroundColor: '#DA0000',
    },
    backgroundColor: '#FF0000',
    color: 'white',
    height: 30,
    padding: 5,
    float: 'right',
    margin: 5,
  },
});

const GoldenTransferPlayerView = ({ transferPlayerId, isNewTransferPlayer, history }) => {
  const historyPath = history.location.search;

  const [claimFilter, setClaimFilter] = useState('all');
  const [selectedClaimId, setSelectedClaimId] = useState(null);
  const [claimStatuses, setClaimStatuses] = useState(isNewTransferPlayer ? blankSchema(TRANSFER_PLAYER_SCHEMA) : {});
  const [overrides, setOverrides] = useState({});
  const [claimMode, setClaimMode] = useState(isNewTransferPlayer ? CLAIM_MODE_NEW_ENTITY : CLAIM_MODE_VIEW);

  const setNewEntityMode = () => {
    setClaimMode(CLAIM_MODE_NEW_ENTITY);
    setClaimStatuses(blankSchema(TRANSFER_PLAYER_SCHEMA));
  };

  const setViewMode = () => {
    setClaimMode(CLAIM_MODE_VIEW);
    setClaimStatuses({});
  };

  useEffect(() => (historyPath === '?new=new' ? setNewEntityMode() : setViewMode()), [historyPath]);

  const tableContatinerClasses = useStyle();
  const buttonClasses = useStyleButton();
  const { loading, error, data, refetch } = useQuery(TRANSFER_PLAYERS_AND_CLAIMS, {
    variables: {
      transfer_player_id: transferPlayerId,
    },
  });

  if (loading) return <Progress />;
  let allClaims = [];

  data &&
    data.statsbomb_transfer_player &&
    data.statsbomb_transfer_player[0] &&
    data.statsbomb_transfer_player[0].mapping_transfer_players.forEach(mappingEntry => {
      allClaims = [...allClaims, ...mappingEntry.claim_transfer_players];
    });

  let filteredClaims = allClaims;
  if (claimFilter === 'undecided') {
    filteredClaims = allClaims.filter(c => {
      return c.approved_at === null && c.rejected_at === null;
    });
  } else if (claimFilter === 'approved') {
    filteredClaims = allClaims.filter(c => c.approved_at != null);
  } else if (claimFilter === 'rejected') {
    filteredClaims = allClaims.filter(c => c.rejected_at != null);
  }

  const selectAClaim = claimUUID => {
    setSelectedClaimId(claimUUID);
    const currentDecisions = {}; // REDUCE

    const claim = allClaims.filter(c => {
      return c.claim_transfer_player_uuid === claimUUID;
    })[0];
    if (claim) {
      TRANSFER_PLAYER_SCHEMA.forEach(row => {
        if (claim && claim[row.columnKey] != null) {
          currentDecisions[row.columnKey] =
            row.mapping_function && row.mapping_function(claim) == null ? 'Ignored' : 'Approved';
        }
      });
    }
    setClaimStatuses(currentDecisions);

    const initialOverrides = {};
    TRANSFER_PLAYER_SCHEMA.forEach(row => {
      initialOverrides[row.columnKey] = null;
    });
    setOverrides(initialOverrides);

    setClaimMode(claimUUID ? CLAIM_MODE_EDIT : CLAIM_MODE_VIEW);
  };

  const changeClaimFilter = claimFilter => {
    selectAClaim(null);
    setClaimFilter(claimFilter);
  };

  const newClaim = () => {
    selectAClaim(null);
    setClaimMode(CLAIM_MODE_NEW_CLAIM);
    setClaimStatuses(blankSchema(TRANSFER_PLAYER_SCHEMA));
  };

  const getGoldenTransferPlayerId = insertedTransfer =>
    insertedTransfer?.data?.insert_statsbomb_transfer_player?.returning?.[0]?.transfer_player_id;

  const claimSelected = claim => {
    selectAClaim(claim.claim_transfer_player_uuid);
  };

  return (
    <>
      <Error message="" error={error} />
      <div className="container-row">
        <h2>Transfer Singular: {isNewTransferPlayer ? ' New Transfer' : transferPlayerId}</h2>

        {claimMode !== CLAIM_MODE_NEW_ENTITY && (
          <TableContainer component={Paper}>
            <ClaimFilters claimFilter={claimFilter} filterResultsFn={changeClaimFilter} />
            <ClaimHistoryTable
              entityType="transfer_player"
              schema={TRANSFER_PLAYER_CLAIM_SCHEMA}
              entities={filteredClaims}
              identifier={{
                headerValue: 'Transfer Name',
                bodyValue: `${data.statsbomb_transfer_player[0]?.statsbomb_player.player_name}`,
              }}
              rowClickHandler={claimSelected}
              selectedEntityKey={selectedClaimId}
            />
            <Button className={buttonClasses.button} variant="contained" color="primary" onClick={() => newClaim()}>
              New Claim
            </Button>
          </TableContainer>
        )}
        {claimMode !== CLAIM_MODE_NEW_ENTITY && (
          <MappingView
            type="transfer_player"
            recordId={transferPlayerId}
            schema={MAPPED_TRANSFER_PLAYER_SCHEMA}
            subscription={VIEW_TRANSFER_PLAYER_MAPPINGS}
            deleteMutation={DELETE_TRANSFER_PLAYER_MAPPING}
            deleteEntityMutation={DELETE_TRANSFER_PLAYER}
            entityKey="provider_team_id"
            styles={{ tableContatinerClasses, buttonClasses }}
          />
        )}
        <TableContainer classes={tableContatinerClasses} component={Paper}>
          <h3>Transfer Attributes {selectedClaimId && ' And Claim Values'}</h3>

          <SingleClaimTable
            goldenEntity={data.statsbomb_transfer_player[0]}
            claim={
              allClaims.filter(c => {
                return c.claim_transfer_player_uuid === selectedClaimId;
              })[0]
            }
            rowSchema={TRANSFER_PLAYER_SCHEMA}
            saveMutation={SAVE_TRANSFER_PLAYER_CLAIM}
            overrideMutation={OVERRIDE_TRANSFER_PLAYER_CLAIM}
            createMutation={INSERT_GOLDEN_TRANSFER_PLAYER}
            sourcePK="provider_transfer_player_id"
            claimPK="claim_transfer_player_uuid"
            goldenEntityPK="transfer_player_id"
            getGoldenEntityId={getGoldenTransferPlayerId}
            entityType="transfer_player"
            setClaimModeView={() => setClaimMode(CLAIM_MODE_VIEW)}
            {...{
              claimStatuses,
              setClaimStatuses,
              selectAClaim,
              overrides,
              setOverrides,
              claimMode,
              refetch,
            }}
          />
        </TableContainer>
      </div>
    </>
  );
};

const TransferPlayer = ({ history }) => {
  const query = new URLSearchParams(useLocation().search);
  const isNewTransferPlayer = query.get('new');
  const transferPlayerId = isNewTransferPlayer ? '0' : query.get('transfer_player_id');

  return <GoldenTransferPlayerView isNewTransferPlayer={!!isNewTransferPlayer} {...{ transferPlayerId, history }} />;
};

export default TransferPlayer;
